import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DirectSaleSheet } from './DirectSaleSheet';
import { useIsPhone } from './useIsPhone';

/**
 * Shared shell for the Direct Sale tool panels (upload / text / motifs),
 * matching the design mock's `.ds-toolpanel`: from 981px it floats as a
 * drawer over the canvas' left edge; below that it is a bottom sheet, so
 * the canvas stays visible above it while the customer edits.
 *
 * Overlay instead of layout displacement on desktop on purpose: pushing the
 * canvas aside would resize its parent and fight the CanvasEditor's
 * ResizeObserver (cf. docs/solutions/ui-bugs/wrong-product-mockup-edit-placement.md).
 */
export function DirectSaleToolPanel({
  title,
  onClose,
  headerAction,
  children,
}: {
  title: string;
  onClose: () => void;
  /** Optional control rendered in the header next to the close button. */
  headerAction?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();

  // One shell at a time: rendering both would mount the panel content twice,
  // and two text panels would each bind to — and create — canvas objects.
  if (isPhone) {
    return (
      <DirectSaleSheet
        title={title}
        onClose={onClose}
        headerAction={headerAction}
      >
        {children}
      </DirectSaleSheet>
    );
  }

  return (
    <div className="mx-3 mb-2 rounded-section border border-line bg-paper-2 shadow-[0_8px_24px_rgba(0,0,0,0.1)] min-[981px]:absolute min-[981px]:left-3 min-[981px]:top-2 min-[981px]:z-10 min-[981px]:m-0 min-[981px]:max-h-[calc(100%-1rem)] min-[981px]:w-80 min-[981px]:animate-in min-[981px]:overflow-y-auto min-[981px]:slide-in-from-left-2 min-[981px]:duration-200">
      <div className="flex items-center gap-1 border-b border-line px-3.5 py-3">
        <span className="flex-1 text-[13px] font-bold text-ink">{title}</span>
        {headerAction}
        <button
          type="button"
          aria-label={t('directSale.design.close')}
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-control text-mute transition-colors hover:bg-paper-2"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {children}
    </div>
  );
}
